import React, { useEffect, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "./context/AuthContext";
import { TranslationProvider } from "./context/TranslationContext";
import { CompanySettingsProvider } from "./context/CompanySettingsContext";
import { ProtectedRoute } from "./components/admin/ProtectedRoute";
import CookieConsent from "./components/common/CookieConsent";

import { checkApiHealth, API_CONFIG, safeFetch } from "./config/api";

// Log API configuration on app load
console.log('[APP] API Configuration:', {
  mode: API_CONFIG.IS_PRODUCTION ? 'PRODUCTION' : 'DEVELOPMENT',
  sameOrigin: API_CONFIG.SAME_ORIGIN,
  baseUrl: API_CONFIG.BASE_URL || '(same-origin /api)'
});

if (API_CONFIG.IS_PRODUCTION) {
  console.log('[APP] Production mode: All API calls use same-origin /api');
}

// Redirect component for old /city/ URLs to new /cities/ URLs
const CityRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={`/cities/${slug}`} replace />;
};

// Router for country hub pages matching /cities-in-{country}
const CitiesInCountryRouter = () => {
  const { "*": wildcardPath } = useParams();
  // If path matches cities-in-{country}, render CountryHubPage
  if (wildcardPath && wildcardPath.startsWith('cities-in-')) {
    const countrySlug = wildcardPath.replace('cities-in-', '');
    return <CountryHubPage countrySlug={countrySlug} />;
  }
  // Not a cities-in route, show 404 or redirect
  return <Navigate to="/" replace />;
};

// Public Pages
import HomePage from "./pages/HomePage";
const CountriesPage = React.lazy(() => import("./pages/CountriesPage"));
const CitiesPage = React.lazy(() => import("./pages/CitiesPage"));
const VisaHubPage = React.lazy(() => import("./pages/VisaHubPage"));
const VisaGuidePage = React.lazy(() => import("./pages/VisaGuidePage"));
const CountryDetailPage = React.lazy(() => import("./pages/CountryDetailPage"));
const CityDetailPage = React.lazy(() => import("./pages/CityDetailPage"));
const ToolsPage = React.lazy(() => import("./pages/ToolsPage"));
const ComparePage = React.lazy(() => import("./pages/ComparePage"));
const CalculatorPage = React.lazy(() => import("./pages/CalculatorPage"));
const SalaryCalculatorPage = React.lazy(() => import("./pages/SalaryCalculatorPage"));
const CityComparisonPage = React.lazy(() => import("./pages/CityComparisonPage"));
const CityComparisonToolPage = React.lazy(() => import("./pages/CityComparisonToolPage"));
const BlogPage = React.lazy(() => import("./pages/BlogPage"));
const BlogPostPage = React.lazy(() => import("./pages/BlogPostPage"));
const TermsPage = React.lazy(() => import("./pages/TermsPage"));
const PrivacyPage = React.lazy(() => import("./pages/PrivacyPage"));
const CookiesPage = React.lazy(() => import("./pages/CookiesPage"));

// Programmatic SEO Pages
const CostOfLivingPage = React.lazy(() => import("./pages/seo/CostOfLivingPage"));
const SalaryNeededPage = React.lazy(() => import("./pages/seo/SalaryNeededPage"));
const LivingGuidePage = React.lazy(() => import("./pages/seo/LivingGuidePage"));
const DigitalNomadCityPage = React.lazy(() => import("./pages/seo/DigitalNomadCityPage"));
const AverageSalaryPage = React.lazy(() => import("./pages/seo/AverageSalaryPage"));
const CountryHubPage = React.lazy(() => import("./pages/seo/CountryHubPage"));
const CostOfLivingCountryPage = React.lazy(() => import("./pages/seo/CostOfLivingCountryPage"));

// Ranking Pages
const CheapestCitiesPage = React.lazy(() => import("./pages/rankings/CheapestCitiesPage"));
const BestCitiesPage = React.lazy(() => import("./pages/rankings/BestCitiesPage"));
const BestNomadCitiesPage = React.lazy(() => import("./pages/rankings/BestNomadCitiesPage"));
const CheapestEuropePage = React.lazy(() => import("./pages/rankings/CheapestEuropePage"));
const CheapestAsiaPage = React.lazy(() => import("./pages/rankings/CheapestAsiaPage"));
const BestEuropePage = React.lazy(() => import("./pages/rankings/BestEuropePage"));
const BestAsiaPage = React.lazy(() => import("./pages/rankings/BestAsiaPage"));
const BestCountriesPage = React.lazy(() => import("./pages/rankings/BestCountriesPage"));
const NomadVisaCountriesPage = React.lazy(() => import("./pages/rankings/NomadVisaCountriesPage"));
const RankingsHubPage = React.lazy(() => import("./pages/RankingsHubPage"));

// Tool Pages
const CostComparisonToolPage = React.lazy(() => import("./pages/tools/CostComparisonToolPage"));
const DigitalNomadVisaChecker = React.lazy(() => import("./pages/tools/DigitalNomadVisaChecker"));
const RentAffordabilityCalculator = React.lazy(() => import("./pages/tools/RentAffordabilityCalculator"));

// Country SEO Pages
const CountryComparisonPage = React.lazy(() => import("./pages/CountryComparisonPage"));
const MoveToCountryGuidePage = React.lazy(() => import("./pages/MoveToCountryGuidePage"));

// HTML Sitemap Page
const HTMLSitemapPage = React.lazy(() => import("./pages/HTMLSitemapPage"));
const FeedbackResponsePage = React.lazy(() => import("./pages/FeedbackResponsePage"));

// City Relocation Pages
const CityRelocationPage = React.lazy(() => import("./pages/CityRelocationPage"));

// Admin Pages
const AdminLoginPage = React.lazy(() => import("./pages/admin/AdminLoginPage"));
const AdminLayout = React.lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = React.lazy(() => import("./pages/admin/AdminDashboard"));
const AdminSEODashboard = React.lazy(() => import("./pages/admin/AdminSEODashboard"));
const AdminCountries = React.lazy(() => import("./pages/admin/AdminCountries"));
const AdminCities = React.lazy(() => import("./pages/admin/AdminCities"));
const AdminNeighborhoods = React.lazy(() => import("./pages/admin/AdminNeighborhoods"));
const AdminBlog = React.lazy(() => import("./pages/admin/AdminBlog"));
const AdminCompanySettings = React.lazy(() => import("./pages/admin/AdminCompanySettings"));
const AdminDataTools = React.lazy(() => import("./pages/admin/AdminDataTools"));
const AdminFailedData = React.lazy(() => import("./pages/admin/AdminFailedData"));
const AdminContentStudio = React.lazy(() => import("./pages/admin/AdminContentStudio"));
const AdminContentStudioPrompts = React.lazy(() => import("./pages/admin/AdminContentStudioPrompts"));
const AdminVisa = React.lazy(() => import("./pages/admin/AdminVisa"));
const B2BContactMessagesPage = React.lazy(() => import("./pages/b2b/B2BContactMessagesPage"));
const B2BAgreementsPage = React.lazy(() => import("./pages/b2b/B2BAgreementsPage"));
const B2BAgreementTemplateEditorPage = React.lazy(() => import("./pages/b2b/B2BAgreementTemplateEditorPage"));
const B2BPartnerOnboardingPage = React.lazy(() => import("./pages/b2b/B2BPartnerOnboardingPage"));
// Customer Experience (CX) module — Phase 1
const CXEmailTemplatesPage = React.lazy(() => import("./pages/b2b/cx/CXEmailTemplatesPage"));
const CXSurveysPage = React.lazy(() => import("./pages/b2b/cx/CXSurveysPage"));
const CXComingSoonPage = React.lazy(() => import("./pages/b2b/cx/CXComingSoonPage"));
const CXJourneysPage = React.lazy(() => import("./pages/b2b/cx/CXJourneysPage"));
const CXWorkflowBuilderPage = React.lazy(() => import("./pages/b2b/cx/CXWorkflowBuilderPage"));
const CXResponsesPage = React.lazy(() => import("./pages/b2b/cx/CXResponsesPage"));
const CXEmailLogsPage = React.lazy(() => import("./pages/b2b/cx/CXEmailLogsPage"));
const CXHealthPage = React.lazy(() => import("./pages/b2b/cx/CXHealthPage"));
const CXSLAPage = React.lazy(() => import("./pages/b2b/cx/CXSLAPage"));
const CXPartnerQualityPage = React.lazy(() => import("./pages/b2b/cx/CXPartnerQualityPage"));
const CXOperationsPage = React.lazy(() => import("./pages/b2b/cx/CXOperationsPage"));
const OpsWorkspacePage = React.lazy(() => import("./pages/b2b/ops/OpsWorkspacePage"));
const CustomerPortalPage = React.lazy(() => import("./pages/public/CustomerPortalPage"));
import { Workflow, MessageSquareText, Timer, Gauge, MailCheck } from "lucide-react";

// About and Contact Pages
const AboutPage = React.lazy(() => import("./pages/AboutPage"));
const ContactPage = React.lazy(() => import("./pages/ContactPage"));
const DataSourcesPage = React.lazy(() => import("./pages/DataSourcesPage"));

// B2B Portal Pages
import { B2BAuthProvider } from "./context/B2BAuthContext";
const B2BLoginPage = React.lazy(() => import("./pages/b2b/B2BLoginPage"));
const B2BDashboard = React.lazy(() => import("./pages/b2b/B2BDashboard"));
const B2BLeadsPage = React.lazy(() => import("./pages/b2b/B2BLeadsPage"));
const B2BLeadDetailPage = React.lazy(() => import("./pages/b2b/B2BLeadDetailPage"));
const B2BInvoicesPage = React.lazy(() => import("./pages/b2b/B2BInvoicesPage"));
const B2BUsersPage = React.lazy(() => import("./pages/b2b/B2BUsersPage"));
const B2BProposalsPage = React.lazy(() => import("./pages/b2b/B2BProposalsPage"));
const B2BUnifiedServiceCatalogPage = React.lazy(() => import("./pages/b2b/B2BUnifiedServiceCatalogPage"));
const B2BProposalTemplatesPage = React.lazy(() => import("./pages/b2b/B2BProposalTemplatesPage"));
const B2BDealsPage = React.lazy(() => import("./pages/b2b/B2BDealsPage"));
const B2BPaymentsPage = React.lazy(() => import("./pages/b2b/B2BPaymentsPage"));
const B2BPartnersPage = React.lazy(() => import("./pages/b2b/B2BPartnersPage"));
const B2BAnalyticsPage = React.lazy(() => import("./pages/b2b/B2BAnalyticsPage"));
const B2BLandingPages = React.lazy(() => import("./pages/b2b/B2BLandingPages"));

// V2 (Commission-Collection) pages — gated by REACT_APP_USE_B2B_V2 feature flag.
const B2BDashboardV2 = React.lazy(() => import("./pages/b2b/v2/B2BDashboardV2"));
const B2BDealsPageV2 = React.lazy(() => import("./pages/b2b/v2/B2BDealsPageV2"));
const B2BPaymentsPageV2 = React.lazy(() => import("./pages/b2b/v2/B2BPaymentsPageV2"));
const USE_V2 = process.env.REACT_APP_USE_B2B_V2 === 'true';
const ActiveDashboard = USE_V2 ? B2BDashboardV2 : B2BDashboard;
const ActiveDealsPage = USE_V2 ? B2BDealsPageV2 : B2BDealsPage;
const ActivePaymentsPage = USE_V2 ? B2BPaymentsPageV2 : B2BPaymentsPage;

// Guide Pages
const GuidesHubPage = React.lazy(() => import("./pages/guides/GuidesHubPage"));
const BestCountriesToLiveIn = React.lazy(() => import("./pages/guides/BestCountriesToLiveIn"));
const BestCountriesForDigitalNomads = React.lazy(() => import("./pages/guides/BestCountriesForDigitalNomads"));
const CheapestCountriesToLiveIn = React.lazy(() => import("./pages/guides/CheapestCountriesToLiveIn"));
const SafestCountriesToLiveIn = React.lazy(() => import("./pages/guides/SafestCountriesToLiveIn"));
const BestCountriesToRetireAbroad = React.lazy(() => import("./pages/guides/BestCountriesToRetireAbroad"));
const BestCountriesForRemoteWorkers = React.lazy(() => import("./pages/guides/BestCountriesForRemoteWorkers"));
const CountriesWithLowestCostOfLiving = React.lazy(() => import("./pages/guides/CountriesWithLowestCostOfLiving"));
const BestCountriesToLiveInEurope = React.lazy(() => import("./pages/guides/BestCountriesToLiveInEurope"));
const BestCountriesToLiveInAsia = React.lazy(() => import("./pages/guides/BestCountriesToLiveInAsia"));
const HowToMoveAbroad = React.lazy(() => import("./pages/guides/HowToMoveAbroad"));
const HowMuchMoneyToLiveAbroad = React.lazy(() => import("./pages/guides/HowMuchMoneyToLiveAbroad"));
const BestCitiesForExpats = React.lazy(() => import("./pages/guides/BestCitiesForExpats"));
const BestCitiesForDigitalNomads = React.lazy(() => import("./pages/guides/BestCitiesForDigitalNomads"));
const CheapestCitiesToLiveIn = React.lazy(() => import("./pages/guides/CheapestCitiesToLiveIn"));
const BestCitiesInSpain = React.lazy(() => import("./pages/guides/BestCitiesInSpain"));
const BestCitiesInThailand = React.lazy(() => import("./pages/guides/BestCitiesInThailand"));
const BestCitiesInPortugal = React.lazy(() => import("./pages/guides/BestCitiesInPortugal"));
const BestCitiesInMexico = React.lazy(() => import("./pages/guides/BestCitiesInMexico"));
const BestCitiesInUAE = React.lazy(() => import("./pages/guides/BestCitiesInUAE"));
const CostOfLivingDubaiVsLondon = React.lazy(() => import("./pages/guides/CostOfLivingDubaiVsLondon"));
const CostOfLivingSpainVsPortugal = React.lazy(() => import("./pages/guides/CostOfLivingSpainVsPortugal"));
const CostOfLivingThailandVsVietnam = React.lazy(() => import("./pages/guides/CostOfLivingThailandVsVietnam"));
const CostOfLivingBaliVsPhuket = React.lazy(() => import("./pages/guides/CostOfLivingBaliVsPhuket"));
const CostOfLivingMexicoVsSpain = React.lazy(() => import("./pages/guides/CostOfLivingMexicoVsSpain"));
const CostOfLivingDubaiVsSingapore = React.lazy(() => import("./pages/guides/CostOfLivingDubaiVsSingapore"));
const CostOfLivingNewYorkVsLondon = React.lazy(() => import("./pages/guides/CostOfLivingNewYorkVsLondon"));
const CostOfLivingBarcelonaVsMadrid = React.lazy(() => import("./pages/guides/CostOfLivingBarcelonaVsMadrid"));
const CostOfLivingLisbonVsPorto = React.lazy(() => import("./pages/guides/CostOfLivingLisbonVsPorto"));
const CostOfLivingBangkokVsKualaLumpur = React.lazy(() => import("./pages/guides/CostOfLivingBangkokVsKualaLumpur"));

// Landing Pages (Meta Ads)
const BaliCompanySetupPage = React.lazy(() => import("./pages/landing/BaliCompanySetupPage"));
const BaliSirketKurulumuPage = React.lazy(() => import("./pages/landing/BaliSirketKurulumuPage"));
const BaliVisaPage = React.lazy(() => import("./pages/landing/BaliVisaPage"));
const JapanBusinessManagerVisaPage = React.lazy(() => import("./pages/landing/JapanBusinessManagerVisaPage"));
const USABusinessImmigrationPage = React.lazy(() => import("./pages/landing/USABusinessImmigrationPage"));
const AmerikaIsGocmenligiPage = React.lazy(() => import("./pages/landing/AmerikaIsGocmenligiPage"));
const UKImmigrationServicesPage = React.lazy(() => import("./pages/landing/UKImmigrationServicesPage"));
const IngiltereGocmenlikHizmetleriPage = React.lazy(() => import("./pages/landing/IngiltereGocmenlikHizmetleriPage"));
const MaltaDilOkuluPage = React.lazy(() => import("./pages/landing/MaltaDilOkuluPage"));
const DubaiYatirimPage = React.lazy(() => import("./pages/landing/DubaiYatirimPage"));
const YunanistanGoldenVisaPage = React.lazy(() => import("./pages/landing/YunanistanGoldenVisaPage"));
const KibrisEmlakPage = React.lazy(() => import("./pages/landing/KibrisEmlakPage"));
const UkCompanyFormationPage = React.lazy(() => import("./pages/landing/UkCompanyFormationPage"));
const DubaiInvestmentPage = React.lazy(() => import("./pages/landing/DubaiInvestmentPage"));
const DubaiDilOkuluPage = React.lazy(() => import("./pages/landing/DubaiDilOkuluPage"));
const DubaiSirketKurulumuPage = React.lazy(() => import("./pages/landing/DubaiSirketKurulumuPage"));
const DubaiCompanyFormationPage = React.lazy(() => import("./pages/landing/DubaiCompanyFormationPage"));
const IrlandaDilOkuluPage = React.lazy(() => import("./pages/landing/IrlandaDilOkuluPage"));
const TurkeyCitizenshipPage = React.lazy(() => import("./pages/landing/TurkeyCitizenshipPage"));
const AmerikaSirketKurulumuPage = React.lazy(() => import("./pages/landing/AmerikaSirketKurulumuPage"));
const UsCompanyFormationPage = React.lazy(() => import("./pages/landing/UsCompanyFormationPage"));
const EstoniaCompanyFormationPage = React.lazy(() => import("./pages/landing/EstoniaCompanyFormationPage"));
const HongKongCompanyFormationPage = React.lazy(() => import("./pages/landing/HongKongCompanyFormationPage"));
const PortugalResidencyProgramsPage = React.lazy(() => import("./pages/landing/PortugalResidencyProgramsPage"));
const PortekizOturumProgramlariPage = React.lazy(() => import("./pages/landing/PortekizOturumProgramlariPage"));
const BulgariaCompanyFormationPage = React.lazy(() => import("./pages/landing/BulgariaCompanyFormationPage"));
const MontenegroCompanyFormationPage = React.lazy(() => import("./pages/landing/MontenegroCompanyFormationPage"));
const JapanCompanyFormationPage = React.lazy(() => import("./pages/landing/JapanCompanyFormationPage"));
const PolandCompanyFormationPage = React.lazy(() => import("./pages/landing/PolandCompanyFormationPage"));
const SpainDigitalNomadVisaPage = React.lazy(() => import("./pages/landing/SpainDigitalNomadVisaPage"));
const IspanyaDigitalNomadVizesiPage = React.lazy(() => import("./pages/landing/IspanyaDigitalNomadVizesiPage"));
const PortugalGoldenVisaPage = React.lazy(() => import("./pages/landing/PortugalGoldenVisaPage"));
const PortekizGoldenVizePage = React.lazy(() => import("./pages/landing/PortekizGoldenVizePage"));
const PolonyaSirketKurulusuPage = React.lazy(() => import("./pages/landing/PolonyaSirketKurulusuPage"));
const JaponyaSirketKurulusuPage = React.lazy(() => import("./pages/landing/JaponyaSirketKurulusuPage"));
const KaradagSirketKurulusuPage = React.lazy(() => import("./pages/landing/KaradagSirketKurulusuPage"));
const BulgaristanSirketKurulusuPage = React.lazy(() => import("./pages/landing/BulgaristanSirketKurulusuPage"));
const IngiltereSirketKurulumuPage = React.lazy(() => import("./pages/landing/IngiltereSirketKurulumuPage"));
const EstonyaSirketKurulumuPage = React.lazy(() => import("./pages/landing/EstonyaSirketKurulumuPage"));
const IngiltereGayrimenkulPage = React.lazy(() => import("./pages/landing/IngiltereGayrimenkulPage"));
const UKPropertyInvestmentPage = React.lazy(() => import("./pages/landing/UKPropertyInvestmentPage"));
const ServicesDirectoryPage = React.lazy(() => import("./pages/ServicesDirectoryPage"));
const CountryServicesPage = React.lazy(() => import("./pages/CountryServicesPage"));
const RigaWaterfrontPage = React.lazy(() => import("./pages/landing/RigaWaterfrontPage"));
const RigaWaterfrontPageEn = React.lazy(() => import("./pages/landing/RigaWaterfrontPageEn"));
import ProposalPublicPage from "./modules/proposals/pages/ProposalPublicPage";

// B2B Pages (Pipeline + Settings)
const B2BPipelinePage = React.lazy(() => import("./pages/b2b/B2BPipelinePage"));
const B2BSettingsPage = React.lazy(() => import("./pages/b2b/B2BSettingsPage"));
const B2BInvoiceTemplateEditorPage = React.lazy(() => import("./pages/b2b/B2BInvoiceTemplateEditorPage"));

// Common Components
import StickyCompareBar from "./components/common/StickyCompareBar";
import ScrollToTop from "./components/common/ScrollToTop";
import AutoCanonical from "./components/common/AutoCanonical";

// REMOVED: ServerStatusOverlay - We no longer block UI waiting for backend
// Frontend loads immediately, data fetches asynchronously with graceful degradation

function App() {
  // Health check and seed database on first load
  useEffect(() => {
    const initializeApp = async () => {
      // Run health check
      const healthResult = await checkApiHealth();
      if (!healthResult.ok) {
        console.error('[APP] API health check failed:', healthResult.error);
        console.warn('[APP] Some features may not work correctly');
      } else {
        console.log('[APP] API health check passed');
      }
      
      // Seed database
      try {
        await safeFetch(`/api/seed`, { method: 'POST' });
      } catch (error) {
        console.error('Failed to seed database:', error);
      }
    };
    initializeApp();
  }, []);

  return (
    <HelmetProvider>
        <AuthProvider>
          <div className="App">
            <BrowserRouter>
              <ScrollToTop />
              <AutoCanonical />
              <CompanySettingsProvider>
              <TranslationProvider>
              <Suspense fallback={null}>
              <Routes>
              {/* Public Routes */}
              <Route path="/" element={<HomePage />} />
              {/* Public Customer Portal (token-based, no auth) */}
              <Route path="/s/:token" element={<CustomerPortalPage />} />
              <Route path="/countries" element={<CountriesPage />} />
              <Route path="/cities" element={<CitiesPage />} />
              <Route path="/visa" element={<VisaHubPage />} />
              <Route path="/visa/:slug" element={<VisaGuidePage />} />
              <Route path="/move-to/:slug" element={<MoveToCountryGuidePage />} />

              {/* Services Directory (additive — separate from existing country/city pages) */}
              <Route path="/services" element={<ServicesDirectoryPage />} />
              <Route path="/services/:countrySlug" element={<CountryServicesPage />} />
              
              {/* City Pages */}
              <Route path="/cities/:slug" element={<CityDetailPage />} />
              {/* Redirect old /city/ URLs to new /cities/ */}
              <Route path="/city/:slug" element={<CityRedirect />} />
              
              {/* Programmatic SEO Pages */}
              <Route path="/cost-of-living/:slug" element={<CostOfLivingPage />} />
              <Route path="/cost-of-living-in/:countrySlug" element={<CostOfLivingCountryPage />} />
              <Route path="/salary-needed/:slug" element={<SalaryNeededPage />} />
              <Route path="/living-in/:slug" element={<LivingGuidePage />} />
              <Route path="/digital-nomad/:slug" element={<DigitalNomadCityPage />} />
              <Route path="/average-salary/:slug" element={<AverageSalaryPage />} />
              
              {/* Tools */}
              <Route path="/tools" element={<ToolsPage />} />
              <Route path="/tools/compare" element={<ComparePage />} />
              <Route path="/tools/calculator" element={<CalculatorPage />} />
              <Route path="/tools/salary-needed-calculator" element={<SalaryCalculatorPage />} />
              <Route path="/tools/digital-nomad-visa-checker" element={<DigitalNomadVisaChecker />} />
              <Route path="/tools/rent-affordability-calculator" element={<RentAffordabilityCalculator />} />
              
              {/* City Comparison Pages (dynamic SEO) */}
              <Route path="/tools/city-comparison" element={<CityComparisonToolPage />} />
              <Route path="/compare/:comparison" element={<CityComparisonPage />} />
              
              {/* Country Comparison Pages (programmatic SEO) */}
              <Route path="/compare-country/:comparison" element={<CountryComparisonPage />} />
              
              {/* City Relocation Pages (programmatic SEO) */}
              <Route path="/moving-from/:relocation" element={<CityRelocationPage />} />
              
              {/* Ranking Pages */}
              <Route path="/rankings" element={<RankingsHubPage />} />
              <Route path="/cheapest-cities-for-digital-nomads" element={<CheapestCitiesPage />} />
              <Route path="/best-cities-for-expats" element={<BestCitiesPage />} />
              <Route path="/best-cities-for-digital-nomads" element={<BestNomadCitiesPage />} />
              <Route path="/cheapest-cities-in-europe" element={<CheapestEuropePage />} />
              <Route path="/cheapest-cities-in-asia" element={<CheapestAsiaPage />} />
              <Route path="/best-cities-in-europe" element={<BestEuropePage />} />
              <Route path="/best-cities-in-asia" element={<BestAsiaPage />} />
              <Route path="/best-countries-for-expats" element={<BestCountriesPage />} />
              <Route path="/countries-with-digital-nomad-visa" element={<NomadVisaCountriesPage />} />
              <Route path="/cost-of-living-comparison" element={<CostComparisonToolPage />} />
              
              {/* Blog */}
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              
              {/* HTML Sitemap - Crawlable */}
              <Route path="/sitemap" element={<HTMLSitemapPage />} />
              <Route path="/feedback/thank-you" element={<FeedbackResponsePage />} />
              <Route path="/feedback/contacted" element={<FeedbackResponsePage response="contacted" />} />
              <Route path="/feedback/not-contacted" element={<FeedbackResponsePage response="not-contacted" />} />

              {/* Guides */}
              <Route path="/guides" element={<GuidesHubPage />} />
              <Route path="/guides/best-countries-to-live-in-2026" element={<BestCountriesToLiveIn />} />
              <Route path="/guides/best-countries-for-digital-nomads" element={<BestCountriesForDigitalNomads />} />
              <Route path="/guides/cheapest-countries-to-live-in-2026" element={<CheapestCountriesToLiveIn />} />
              <Route path="/guides/safest-countries-to-live-in-the-world" element={<SafestCountriesToLiveIn />} />
              <Route path="/guides/best-countries-to-retire-abroad" element={<BestCountriesToRetireAbroad />} />
              <Route path="/guides/best-countries-for-remote-workers" element={<BestCountriesForRemoteWorkers />} />
              <Route path="/guides/countries-with-lowest-cost-of-living" element={<CountriesWithLowestCostOfLiving />} />
              <Route path="/guides/best-countries-to-live-in-europe" element={<BestCountriesToLiveInEurope />} />
              <Route path="/guides/best-countries-to-live-in-asia" element={<BestCountriesToLiveInAsia />} />
              <Route path="/guides/how-to-move-abroad" element={<HowToMoveAbroad />} />
              <Route path="/guides/how-much-money-to-live-abroad" element={<HowMuchMoneyToLiveAbroad />} />
              <Route path="/guides/best-cities-for-expats" element={<BestCitiesForExpats />} />
              <Route path="/guides/best-cities-for-digital-nomads" element={<BestCitiesForDigitalNomads />} />
              <Route path="/guides/cheapest-cities-to-live-in-the-world" element={<CheapestCitiesToLiveIn />} />
              <Route path="/guides/best-cities-to-live-in-spain" element={<BestCitiesInSpain />} />
              <Route path="/guides/best-cities-to-live-in-thailand" element={<BestCitiesInThailand />} />
              <Route path="/guides/best-cities-to-live-in-portugal" element={<BestCitiesInPortugal />} />
              <Route path="/guides/best-cities-to-live-in-mexico" element={<BestCitiesInMexico />} />
              <Route path="/guides/best-cities-to-live-in-uae" element={<BestCitiesInUAE />} />
              <Route path="/guides/cost-of-living-dubai-vs-london" element={<CostOfLivingDubaiVsLondon />} />
              <Route path="/guides/cost-of-living-spain-vs-portugal" element={<CostOfLivingSpainVsPortugal />} />
              <Route path="/guides/cost-of-living-thailand-vs-vietnam" element={<CostOfLivingThailandVsVietnam />} />
              <Route path="/guides/cost-of-living-bali-vs-phuket" element={<CostOfLivingBaliVsPhuket />} />
              <Route path="/guides/cost-of-living-mexico-vs-spain" element={<CostOfLivingMexicoVsSpain />} />
              <Route path="/guides/cost-of-living-dubai-vs-singapore" element={<CostOfLivingDubaiVsSingapore />} />
              <Route path="/guides/cost-of-living-new-york-vs-london" element={<CostOfLivingNewYorkVsLondon />} />
              <Route path="/guides/cost-of-living-barcelona-vs-madrid" element={<CostOfLivingBarcelonaVsMadrid />} />
              <Route path="/guides/cost-of-living-lisbon-vs-porto" element={<CostOfLivingLisbonVsPorto />} />
              <Route path="/guides/cost-of-living-bangkok-vs-kuala-lumpur" element={<CostOfLivingBangkokVsKualaLumpur />} />

              {/* Legal Pages */}
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/cookies" element={<CookiesPage />} />

              {/* About and Contact */}
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/data-sources-methodology" element={<DataSourcesPage />} />

              {/* Admin Routes */}
              <Route path="/admin/login" element={<AdminLoginPage />} />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<AdminDashboard />} />
                <Route path="seo" element={<AdminSEODashboard />} />
                <Route path="content-studio" element={<AdminContentStudio />} />
                <Route path="content-studio/prompts" element={<AdminContentStudioPrompts />} />
                <Route path="visa" element={<AdminVisa />} />
                <Route path="countries" element={<AdminCountries />} />
                <Route path="cities" element={<AdminCities />} />
                <Route path="neighborhoods" element={<AdminNeighborhoods />} />
                <Route path="blog" element={<AdminBlog />} />
                <Route path="settings" element={<AdminCompanySettings />} />
                <Route path="data-tools" element={<AdminDataTools />} />
                <Route path="failed-data" element={<AdminFailedData />} />
              </Route>

              {/* B2B Portal Routes */}
              <Route path="/b2b/login" element={
                <B2BAuthProvider>
                  <B2BLoginPage />
                </B2BAuthProvider>
              } />
              <Route path="/b2b" element={
                <B2BAuthProvider>
                  <ActiveDashboard />
                </B2BAuthProvider>
              } />
              <Route path="/b2b/leads" element={
                <B2BAuthProvider>
                  <B2BLeadsPage />
                </B2BAuthProvider>
              } />
              <Route path="/b2b/contact-messages" element={
                <B2BAuthProvider>
                  <B2BContactMessagesPage />
                </B2BAuthProvider>
              } />
              <Route path="/b2b/agreements" element={
                <B2BAuthProvider>
                  <B2BAgreementsPage />
                </B2BAuthProvider>
              } />
              <Route path="/b2b/agreement-templates/:id" element={
                <B2BAuthProvider>
                  <B2BAgreementTemplateEditorPage />
                </B2BAuthProvider>
              } />
              <Route path="/b2b/onboarding" element={
                <B2BAuthProvider>
                  <B2BPartnerOnboardingPage />
                </B2BAuthProvider>
              } />
              <Route path="/b2b/leads/:id" element={
                <B2BAuthProvider>
                  <B2BLeadDetailPage />
                </B2BAuthProvider>
              } />
              {/* Customer Experience (CX) module — Phase 1 */}
              <Route path="/b2b/cx" element={<Navigate to="/b2b/cx/health" replace />} />
              <Route path="/b2b/cx/health" element={
                <B2BAuthProvider><CXHealthPage /></B2BAuthProvider>
              } />
              <Route path="/b2b/cx/email-templates" element={
                <B2BAuthProvider><CXEmailTemplatesPage /></B2BAuthProvider>
              } />
              <Route path="/b2b/cx/surveys" element={
                <B2BAuthProvider><CXSurveysPage /></B2BAuthProvider>
              } />
              <Route path="/b2b/cx/journeys" element={
                <B2BAuthProvider><CXJourneysPage /></B2BAuthProvider>
              } />
              <Route path="/b2b/cx/journeys/:id" element={
                <B2BAuthProvider><CXWorkflowBuilderPage /></B2BAuthProvider>
              } />
              <Route path="/b2b/cx/responses" element={
                <B2BAuthProvider><CXResponsesPage /></B2BAuthProvider>
              } />
              <Route path="/b2b/cx/sla" element={
                <B2BAuthProvider><CXSLAPage /></B2BAuthProvider>
              } />
              <Route path="/b2b/cx/partner-quality" element={
                <B2BAuthProvider><CXPartnerQualityPage /></B2BAuthProvider>
              } />
              <Route path="/b2b/cx/operations" element={
                <B2BAuthProvider><CXOperationsPage /></B2BAuthProvider>
              } />
              <Route path="/b2b/ops/lead/:id" element={
                <B2BAuthProvider><OpsWorkspacePage /></B2BAuthProvider>
              } />
              <Route path="/b2b/cx/email-logs" element={
                <B2BAuthProvider><CXEmailLogsPage /></B2BAuthProvider>
              } />
              <Route path="/b2b/proposals" element={
                <B2BAuthProvider>
                  <B2BProposalsPage />
                </B2BAuthProvider>
              } />
              <Route path="/b2b/service-catalog" element={
                <B2BAuthProvider>
                  <B2BUnifiedServiceCatalogPage />
                </B2BAuthProvider>
              } />
              <Route path="/b2b/services-registry" element={
                <B2BAuthProvider>
                  <B2BUnifiedServiceCatalogPage />
                </B2BAuthProvider>
              } />
              <Route path="/b2b/proposal-templates" element={
                <B2BAuthProvider>
                  <B2BProposalTemplatesPage />
                </B2BAuthProvider>
              } />
              <Route path="/b2b/invoices" element={
                <B2BAuthProvider>
                  <B2BInvoicesPage />
                </B2BAuthProvider>
              } />
              <Route path="/b2b/users" element={
                <B2BAuthProvider>
                  <B2BUsersPage />
                </B2BAuthProvider>
              } />
              <Route path="/b2b/deals" element={
                <B2BAuthProvider>
                  <ActiveDealsPage />
                </B2BAuthProvider>
              } />
              <Route path="/b2b/payments" element={
                <B2BAuthProvider>
                  <ActivePaymentsPage />
                </B2BAuthProvider>
              } />
              <Route path="/b2b/partners" element={
                <B2BAuthProvider>
                  <B2BPartnersPage />
                </B2BAuthProvider>
              } />
              <Route path="/b2b/analytics" element={
                <B2BAuthProvider>
                  <B2BAnalyticsPage />
                </B2BAuthProvider>
              } />
              <Route path="/b2b/landing-pages" element={
                <B2BAuthProvider>
                  <B2BLandingPages />
                </B2BAuthProvider>
              } />
              <Route path="/b2b/pipeline" element={
                <B2BAuthProvider>
                  <B2BPipelinePage />
                </B2BAuthProvider>
              } />
              <Route path="/b2b/settings" element={
                <B2BAuthProvider>
                  <B2BSettingsPage />
                </B2BAuthProvider>
              } />
              <Route path="/b2b/invoice-templates/:id" element={
                <B2BAuthProvider>
                  <B2BInvoiceTemplateEditorPage />
                </B2BAuthProvider>
              } />
              
              {/* Country Hub Pages - catch-all for /cities-in-{country} pattern */}
              {/* Landing Pages (Meta Ads) */}
              <Route path="/bali-company-setup" element={<BaliCompanySetupPage />} />
              <Route path="/bali-sirket-kurulumu" element={<BaliSirketKurulumuPage />} />
              <Route path="/bali-visa" element={<BaliVisaPage />} />
              <Route path="/japan-business-manager-visa" element={<JapanBusinessManagerVisaPage />} />
              <Route path="/usa-business-immigration" element={<USABusinessImmigrationPage />} />
              <Route path="/amerika-is-gocmenligi" element={<AmerikaIsGocmenligiPage />} />
              <Route path="/uk-immigration-services" element={<UKImmigrationServicesPage />} />
              <Route path="/ingiltere-gocmenlik-hizmetleri" element={<IngiltereGocmenlikHizmetleriPage />} />
              <Route path="/malta-dil-okulu" element={<MaltaDilOkuluPage />} />
              <Route path="/dubai-yatirim" element={<DubaiYatirimPage />} />
              <Route path="/yunanistan-golden-visa" element={<YunanistanGoldenVisaPage />} />
              <Route path="/kibris-emlak" element={<KibrisEmlakPage />} />
              <Route path="/uk-company-formation" element={<UkCompanyFormationPage />} />
              <Route path="/dubai-investment" element={<DubaiInvestmentPage />} />
              <Route path="/dubai-dil-okulu" element={<DubaiDilOkuluPage />} />
              <Route path="/dubai-sirket-kurulumu" element={<DubaiSirketKurulumuPage />} />
              <Route path="/dubai-company-formation" element={<DubaiCompanyFormationPage />} />
              <Route path="/irlanda-dil-okulu" element={<IrlandaDilOkuluPage />} />
              <Route path="/turkey-citizenship" element={<TurkeyCitizenshipPage />} />
              <Route path="/amerika-sirket-kurulumu" element={<AmerikaSirketKurulumuPage />} />
              <Route path="/us-company-formation" element={<UsCompanyFormationPage />} />
              <Route path="/estonia-company-formation" element={<EstoniaCompanyFormationPage />} />
              <Route path="/hong-kong-company-formation" element={<HongKongCompanyFormationPage />} />
              <Route path="/portugal-residency-programs" element={<PortugalResidencyProgramsPage />} />
              <Route path="/portekiz-oturum-programlari" element={<PortekizOturumProgramlariPage />} />
              <Route path="/bulgaria-company-formation" element={<BulgariaCompanyFormationPage />} />
              <Route path="/montenegro-company-formation" element={<MontenegroCompanyFormationPage />} />
              <Route path="/japan-company-formation" element={<JapanCompanyFormationPage />} />
              <Route path="/poland-company-formation" element={<PolandCompanyFormationPage />} />
              <Route path="/spain-digital-nomad-visa" element={<SpainDigitalNomadVisaPage />} />
              <Route path="/ispanya-digital-nomad-vizesi" element={<IspanyaDigitalNomadVizesiPage />} />
              <Route path="/portugal-golden-visa" element={<PortugalGoldenVisaPage />} />
              <Route path="/portekiz-golden-vize" element={<PortekizGoldenVizePage />} />
              <Route path="/polonya-sirket-kurulusu" element={<PolonyaSirketKurulusuPage />} />
              <Route path="/japonya-sirket-kurulusu" element={<JaponyaSirketKurulusuPage />} />
              <Route path="/karadag-sirket-kurulusu" element={<KaradagSirketKurulusuPage />} />
              <Route path="/bulgaristan-sirket-kurulusu" element={<BulgaristanSirketKurulusuPage />} />
              <Route path="/ingiltere-sirket-kurulumu" element={<IngiltereSirketKurulumuPage />} />
              <Route path="/estonya-sirket-kurulumu" element={<EstonyaSirketKurulumuPage />} />
              <Route path="/ingiltere-gayrimenkul" element={<IngiltereGayrimenkulPage />} />
              <Route path="/uk-property-investment" element={<UKPropertyInvestmentPage />} />
              <Route path="/letonya-riga-waterfront" element={<RigaWaterfrontPage />} />
              <Route path="/riga-waterfront" element={<RigaWaterfrontPageEn />} />

              {/* Hosted Proposal — public, no-auth view by random token */}
              <Route path="/p/:token" element={<ProposalPublicPage />} />

              {/* Must be at end because React Router v6 doesn't support /cities-in-:country syntax */}
              <Route path="/*" element={<CitiesInCountryRouter />} />
            </Routes>
              </Suspense>
            </TranslationProvider>
            </CompanySettingsProvider>
          <StickyCompareBar />
          <CookieConsent />
          </BrowserRouter>
          <Toaster position="bottom-right" richColors />
        </div>
      </AuthProvider>
  </HelmetProvider>
  );
}

export default App;
